import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type SlotBody =
  paths["/api/v1/viewings"]["post"]["requestBody"]["content"]["application/json"];
type SignupBody =
  paths["/api/v1/viewings/{slot_id}/signups"]["post"]["requestBody"]["content"]["application/json"];

export const viewingKeys = {
  all: ["viewings"] as const,
  detail: (id: string) => ["viewings", "detail", id] as const,
};

export function useViewings(opts?: { enabled?: boolean }) {
  return useQuery({
    enabled: opts?.enabled ?? true,
    queryKey: viewingKeys.all,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/viewings", {});
      if (error || !data) throw new Error("Failed to load viewings");
      return data;
    },
  });
}

export function useViewing(id: string | undefined) {
  return useQuery({
    queryKey: viewingKeys.detail(id ?? ""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/viewings/{slot_id}", {
        params: { path: { slot_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load viewing");
      return data;
    },
  });
}

export function useCreateViewing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SlotBody) => {
      const { data, error } = await apiClient.POST("/api/v1/viewings", { body });
      if (error || !data) throw new Error("Failed to create viewing");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: viewingKeys.all }),
  });
}

export function useAddSignup(slotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SignupBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/viewings/{slot_id}/signups",
        { params: { path: { slot_id: slotId } }, body },
      );
      if (error || !data) throw new Error("Failed to add signup");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: viewingKeys.detail(slotId) }),
  });
}

export function useUpdateSignup(slotId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { signupId: string; status: string }) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/viewings/{slot_id}/signups/{signup_id}",
        {
          params: { path: { slot_id: slotId, signup_id: vars.signupId } },
          body: { status: vars.status },
        },
      );
      if (error || !data) throw new Error("Failed to update signup");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: viewingKeys.detail(slotId) }),
  });
}
